// ThreadList — left sidebar. Shows pinned + recent threads with pin/rename/delete actions.
// New chat at top. Renames inline. Deletes confirm with a single undo-stamp affordance.

import { useEffect, useRef, useState } from 'react';
import * as api from '../api';
import type { Thread } from '../types';

interface Props {
  threads: Thread[];
  activeThreadId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onThreadsChanged: (threads: Thread[]) => void;
  /** Mobile drawer state — slides in from the left when true. Ignored on md+. */
  mobileOpen?: boolean;
}

export function ThreadList({ threads, activeThreadId, onSelect, onNewChat, onThreadsChanged, mobileOpen = false }: Props) {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);

  const pinned = threads.filter((t) => t.pinned);
  const recent = threads.filter((t) => !t.pinned);

  async function handlePin(t: Thread) {
    setMenuOpenId(null);
    const next = await api.setThreadPinned(t.id, !t.pinned);
    onThreadsChanged(threads.map((x) => (x.id === t.id ? next : x)));
  }
  async function handleRename(id: string, newTitle: string) {
    setRenamingId(null);
    const next = await api.renameThread(id, newTitle);
    onThreadsChanged(threads.map((x) => (x.id === id ? next : x)));
  }
  async function handleDelete(t: Thread) {
    setMenuOpenId(null);
    if (!confirm(`Delete thread "${t.title ?? 'Untitled'}"? This cannot be undone.`)) return;
    await api.deleteThread(t.id);
    onThreadsChanged(threads.filter((x) => x.id !== t.id));
  }

  return (
    <aside
      className={`bg-bg/95 md:bg-bg/60 border-r border-hairline/70 flex flex-col w-64 shrink-0 z-30
        fixed md:sticky left-0 top-[57px] bottom-0 md:bottom-auto md:h-[calc(100vh-57px)]
        transition-transform duration-200 ease-out
        md:translate-x-0 ${mobileOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full md:translate-x-0'}`}
    >
      {/* New chat */}
      <div className="px-3 pt-3 pb-2">
        <button
          onClick={onNewChat}
          className="w-full bg-ink text-bg rounded-md py-2.5 px-3 font-sans text-[13px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-accent transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-1.5 pb-3">
        {pinned.length > 0 && (
          <>
            <SectionLabel>Pinned</SectionLabel>
            {pinned.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                active={t.id === activeThreadId}
                renaming={renamingId === t.id}
                menuOpen={menuOpenId === t.id}
                onSelect={() => onSelect(t.id)}
                onRequestRename={() => setRenamingId(t.id)}
                onRenameDone={(title) => handleRename(t.id, title)}
                onRenameCancel={() => setRenamingId(null)}
                onMenuToggle={() => setMenuOpenId(menuOpenId === t.id ? null : t.id)}
                onPin={() => handlePin(t)}
                onDelete={() => handleDelete(t)}
              />
            ))}
          </>
        )}

        {recent.length > 0 && (
          <>
            <SectionLabel className={pinned.length > 0 ? 'mt-3' : ''}>Recent</SectionLabel>
            {recent.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                active={t.id === activeThreadId}
                renaming={renamingId === t.id}
                menuOpen={menuOpenId === t.id}
                onSelect={() => onSelect(t.id)}
                onRequestRename={() => setRenamingId(t.id)}
                onRenameDone={(title) => handleRename(t.id, title)}
                onRenameCancel={() => setRenamingId(null)}
                onMenuToggle={() => setMenuOpenId(menuOpenId === t.id ? null : t.id)}
                onPin={() => handlePin(t)}
                onDelete={() => handleDelete(t)}
              />
            ))}
          </>
        )}

        {threads.length === 0 && (
          <div className="px-3 py-6 text-text3 text-xs italic text-center leading-relaxed">
            No threads yet. Start a chat above.
          </div>
        )}
      </div>
    </aside>
  );
}

function SectionLabel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`text-[11px] text-text3 px-3 pt-3 pb-1.5 ${className}`}>
      {children}
    </div>
  );
}

interface ThreadRowProps {
  thread: Thread;
  active: boolean;
  renaming: boolean;
  menuOpen: boolean;
  onSelect: () => void;
  onRequestRename: () => void;
  onRenameDone: (title: string) => void;
  onRenameCancel: () => void;
  onMenuToggle: () => void;
  onPin: () => void;
  onDelete: () => void;
}

function ThreadRow(props: ThreadRowProps) {
  const { thread, active, renaming, menuOpen } = props;
  const [draftTitle, setDraftTitle] = useState(thread.title ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (renaming) {
      setDraftTitle(thread.title ?? '');
      // Defer focus so the click that triggered rename doesn't immediately blur it
      setTimeout(() => inputRef.current?.focus(), 0);
      setTimeout(() => inputRef.current?.select(), 0);
    }
  }, [renaming, thread.title]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        props.onMenuToggle();
      }
    }
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen, props]);

  function commitRename() {
    const t = draftTitle.trim();
    if (t && t !== thread.title) props.onRenameDone(t);
    else props.onRenameCancel();
  }

  return (
    <div
      className={`group relative rounded-md px-2 transition-colors ${
        active ? 'bg-ink/[0.06]' : 'hover:bg-ink/[0.04]'
      }`}
    >
      {renaming ? (
        <div className="flex items-center gap-1.5 py-1.5">
          <input
            ref={inputRef}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              } else if (e.key === 'Escape') {
                props.onRenameCancel();
              }
            }}
            className="flex-1 bg-card border border-ink/20 rounded px-2 py-1.5 text-[13px] text-ink outline-none focus:border-ink"
            maxLength={120}
          />
        </div>
      ) : (
        <button
          onClick={props.onSelect}
          className="flex items-center w-full gap-1 py-2 text-left"
        >
          {thread.pinned && (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className="text-accent flex-shrink-0">
              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
            </svg>
          )}
          <span className={`flex-1 text-[13px] truncate ${active ? 'text-ink font-medium' : 'text-ink/80'}`}>
            {thread.title ?? 'Untitled'}
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              props.onMenuToggle();
            }}
            role="button"
            aria-label="Thread actions"
            className={`flex-shrink-0 rounded p-1 transition-opacity ${
              menuOpen ? 'opacity-100 bg-ink/10' : 'opacity-0 group-hover:opacity-100 hover:bg-ink/10'
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-text2">
              <circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" />
            </svg>
          </span>
        </button>
      )}

      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute right-2 top-9 w-44 bg-card border border-border rounded-md shadow-md z-30 py-1"
        >
          <MenuItem onClick={props.onPin}>
            {thread.pinned ? 'Unpin' : 'Pin to top'}
          </MenuItem>
          <MenuItem onClick={() => { props.onMenuToggle(); props.onRequestRename(); }}>
            Rename
          </MenuItem>
          <MenuDivider />
          <MenuItem danger onClick={props.onDelete}>
            Delete
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({ children, onClick, danger = false }: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left text-[13px] px-3 py-1.5 transition-colors ${
        danger ? 'text-accent hover:bg-accent/10' : 'text-ink hover:bg-ink/[0.05]'
      }`}
    >
      {children}
    </button>
  );
}

function MenuDivider() {
  return <div className="h-px bg-hairline my-1" />;
}
